/**
 * Shared registers kept row by row (build 5, backend 0091).
 *
 * Several screens kept a whole register as ONE site note, so the last save
 * won and two people saving at once overwrote each other. A register is now
 * a set of rows, each with a version: a change names the version it was
 * made from, and a change made from an old version is refused with who
 * changed the row and when — never written over their change.
 *
 * This file is pure: the row and change shapes, the refusal, and the two
 * functions every screen's save goes through — `docToRows` (a register as
 * the screen holds it, cut into rows by its spec) and `rowChanges` (what a
 * save changed, as row operations carrying the versions the screen read).
 */
import { DataSourceError } from "../api/dataSource";

/** The registers the platform keeps (backend registers/rows.py). Anything else is refused. */
export const REGISTER_KEYS = ["vdgs", "equipment", "facility-inspections", "urgent-inspections", "contracts"] as const;
export type RegisterKey = (typeof REGISTER_KEYS)[number];

export interface RegisterRow<T = Record<string, unknown>> {
  id: string;
  data: T;
  version: number;
  createdAt: string;
  createdBy?: string;
  updatedAt: string;
  updatedBy?: string;
  deletedAt?: string;
  deletedBy?: string;
  /** the note this row was brought across from, when it was */
  fromNote?: string;
}

export interface RegisterRows<T = Record<string, unknown>> {
  register: RegisterKey;
  rows: RegisterRow<T>[];
  /** how many rows the register has ever held, deleted ones included — 0 means a note may be brought across */
  ever: number;
  /** whether the signed-in person may change this register */
  canWrite: boolean;
}

export type RowOp =
  | { op: "put"; id: string; data: Record<string, unknown>; version: number | null }
  | { op: "delete"; id: string; version: number };

export type ConflictKind = "row_changed" | "row_exists" | "row_deleted";

/**
 * A change refused because the row moved on since it was read. Carries the
 * row as it now stands, so the screen can show theirs.
 */
export class RowConflict extends DataSourceError {
  readonly kind: ConflictKind;
  readonly register: RegisterKey;
  readonly rowId: string;
  readonly current?: RegisterRow;
  constructor(
    message: string,
    o: { kind: ConflictKind; register: RegisterKey; rowId: string; current?: RegisterRow; status?: number },
  ) {
    super(message, { status: o.status ?? 409, detail: { error: o.kind, row_id: o.rowId } });
    this.name = "RowConflict";
    this.kind = o.kind;
    this.register = o.register;
    this.rowId = o.rowId;
    this.current = o.current;
  }
}

/* by name as well as by class: a second copy of this module (a split chunk, a test's fresh import) is still the same refusal */
export function isRowConflict(e: unknown): e is RowConflict {
  return e instanceof RowConflict || (e instanceof Error && e.name === "RowConflict" && "rowId" in e);
}

/** How a screen's register becomes rows and back. */
export interface RegisterSpec<D> {
  key: RegisterKey;
  /** the note topic the register was kept under before rows */
  topic: string;
  /** the register cut into rows, by row id; the ids are stable across saves */
  toRows: (doc: D) => Map<string, Record<string, unknown>>;
  /** the register put back together from its rows, in the order they were first written */
  fromRows: (rows: readonly { id: string; data: Record<string, unknown> }[]) => D;
  /** a few words naming one row, for "someone else changed …" */
  describe: (id: string, data: Record<string, unknown> | undefined) => string;
  /**
   * Where the old register was the merge of every note rather than the
   * latest (each device posted its own copy), how to merge them — so what is
   * brought across is everything the screen used to show, not one copy.
   */
  mergeNotes?: (docs: D[]) => D;
}

/* JSON equality, independent of key order, so a row read back is not "changed" by being re-serialised */
function canonical(v: unknown): string {
  if (Array.isArray(v)) return `[${v.map(canonical).join(",")}]`;
  if (v && typeof v === "object") {
    const o = v as Record<string, unknown>;
    return `{${Object.keys(o)
      .filter((k) => o[k] !== undefined)
      .sort()
      .map((k) => `${JSON.stringify(k)}:${canonical(o[k])}`)
      .join(",")}}`;
  }
  return JSON.stringify(v ?? null);
}

export function sameRow(a: unknown, b: unknown): boolean {
  return canonical(a) === canonical(b);
}

/** Drop `undefined` the way JSON would, so what is sent is exactly what is compared. */
export function plain<T>(v: T): T {
  return JSON.parse(JSON.stringify(v)) as T;
}

/**
 * What a save changed, as row operations.
 *
 * `base` is the register the person's change was made FROM — the one drawn on
 * the screen when they pressed save, or when they opened the editor — and
 * `versions` the version of each of its rows at that moment. Only rows that
 * differ between `base` and `next` are sent, each with the version it was read
 * at, so a colleague's change to the same row since is refused rather than
 * overwritten, and a colleague's change to any other row is never touched.
 */
export function rowChanges(
  base: Map<string, Record<string, unknown>>,
  next: Map<string, Record<string, unknown>>,
  versions: ReadonlyMap<string, number>,
): RowOp[] {
  const ops: RowOp[] = [];
  for (const [id, data] of next) {
    const was = base.get(id);
    if (was === undefined) {
      /* a new row: no version, so an id a colleague took meanwhile is refused, not overwritten */
      ops.push({ op: "put", id, data: plain(data), version: null });
    } else if (!sameRow(was, data)) {
      ops.push({ op: "put", id, data: plain(data), version: versions.get(id) ?? null });
    }
  }
  for (const id of base.keys())
    if (!next.has(id)) {
      const v = versions.get(id);
      if (v != null) ops.push({ op: "delete", id, version: v });
    }
  return ops;
}

/** The note's register cut into rows, ready to bring across, in the note's order. */
export function noteRows<D>(spec: RegisterSpec<D>, doc: D): { id: string; data: Record<string, unknown> }[] {
  return [...spec.toRows(doc)].map(([id, data]) => ({ id, data: plain(data) }));
}
